import Head from "next/head";
import Link from "next/link";
import NextScript from "next/script";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "RAVE 52",
    description:
      "Seattle welcomed the world. Now the next chapter takes us to Portland on July 16. Join fellow supporters when the Sounders take on the Timbers and help keep the RAVE 52 spirit moving forward.",
    authRedirectPage: routes.home,
    buttonLabel: "Buy Now",
    buttonVariant: "contained",
    primaryButtonStyles: {
      backgroundColor: "#00FF61",
      border: "2px solid #000000",
      color: "#000000",
      ":hover": {
        backgroundColor: "#00FF61",
        border: "2px solid #000000",
        color: "#000000",
      },
    },
    titleStyles: {
      color: "#000000",
      "@media (max-width: 900px)": {
        fontSize: "1.5rem",
      },
    },
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I get my tickets?",
    answer:
      "Once you complete your purchase, your ticket is secured. You will receive a text confirming delivery to your account 3 hours prior to match start.",
  },
  {
    question: "When will I receive my tickets?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your account.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will vary by match.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Tickets are non-transferable, but can be assigned to others in your party to enter separately.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <a href="mailto:ticketsupport@soundersfc.com">
          ticketsupport@soundersfc.com
        </a>{" "}
        for any assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other fans?",
    answer: (
      <>
        Yes! Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "seattlesounders_upgrades" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the match.
        You will need to ensure your group is created and all members have
        joined at least 4 hours prior to game start in order to be seated with
        one another.
      </>
    ),
  },
  {
    question: "Can I receive a refund if I cannot attend a match?",
    answer:
      "We do not issue any refunds for matches you are not able to attend.",
  },
];

export const PRE_PURCHASE_FAQS: TTeamConfig["LANGUAGES"]["PRE_PURCHASE_FAQS"] =
  [
    {
      question: "How do I get my tickets?",
      answer:
        "Once you complete your purchase, your ticket is secured. You will receive a text confirming delivery to your account 3 hours prior to match start.",
    },
    {
      question: "When will I receive my tickets?",
      answer:
        "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your account.",
    },
    {
      question: "Where will my seats be located?",
      answer: "Seat locations will vary by match.",
    },
    {
      question: "Can these tickets be transferred to a friend?",
      answer:
        "Tickets are non-transferable, but can be assigned to others in your party to enter separately.",
    },
    {
      question: "Can I receive a refund if I cannot attend a match?",
      answer:
        "We do not issue any refunds for matches you are not able to attend.",
    },
  ];

export const REGISTER_TERMS_AND_CONDITION = () => (
  <>
    By creating an account, you agree to receive account-related notification
    and marketing communication from the Seattle Sounders FC and you confirm
    that you have read and agree to the platform&apos;s{" "}
    <Link href={"https://www.attend.tech/terms-of-use"} target={"_blank"}>
      Terms & Conditions
    </Link>{" "}
    and{" "}
    <Link href={"https://www.attend.tech/privacy-policy"} target={"_blank"}>
      Privacy Policy
    </Link>
    .
  </>
);

export const TERMS_AND_CONDITION: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION"] =
  () => (
    <>
      By checking the box you agree to receive notifications regarding your
      purchase and to the{" "}
      <Link href={"https://www.attend.tech/terms-of-use"} target={"_blank"}>
        Terms & Conditions
      </Link>{" "}
      and{" "}
      <Link href={"https://www.attend.tech/privacy-policy"} target={"_blank"}>
        Privacy Policy
      </Link>
      .
    </>
  );

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '493354401187639');
            fbq('track', 'PageView');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try{
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if(cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };
                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'Purchase', {
                  content_name: content_name, 
                  value: String(cartRes.totalAmount), 
                  currency: 'USD', 
                  order_id: checkoutRes.orderNumber,
                });
                console.log('meta pixel purchase event fired.');
              
              } catch(error){
                console.log('meta pixel purchase event error:', error); 
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=493354401187639&ev=PageView&noscript=1"
        />
      </noscript>

      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=DC-15814684"
      />
      <script
        id={`${TEAM_SCHEMA}-floodlight-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'DC-15814684');

            try {
              gtag('event', 'conversion', {
                allow_custom_scripts: true,
                send_to: 'DC-15814684/ssfc/seatt0+standard'
              });
              console.log('floodlight RTG event fired.');
            } catch (error) {
              console.log('floodlight RTG event error:', error);
            }

            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try {
                const { cartRes, checkoutRes } = e.detail;
                gtag('event', 'purchase', {
                  allow_custom_scripts: true,
                  value: cartRes.totalAmount,
                  transaction_id: checkoutRes.orderNumber,
                  send_to: 'DC-15814684/ssfcsale/seatt00+transactions'
                });
                console.log('floodlight purchase event fired.');
              } catch (error) {
                console.log('floodlight purchase event error:', error);
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://ad.doubleclick.net/ddm/activity/src=15814684;type=ssfc;cat=seatt0;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=;gdpr_consent=;ord=1?"
          alt=""
        />
      </noscript>
    </Head>
  );
};
